import { Injectable }                                            from '@angular/core';
import { UntypedFormGroup }                                             from '@angular/forms';
import { NestedVisibilityRule, VisibilityCondition, VisibilityOperator, VisibilityRule } from './visibility-rule.model';
import { isNullOrUndefined }                                     from '@cs/core';

@Injectable()
export class VisibilityRulesService {

	private rules: VisibilityRule[] = [];
	private formGroup: UntypedFormGroup;

	/**
	 * Set the visibility rules configuration
	 */
	setRules(rules: VisibilityRule[]): void {
		this.rules = rules || [];
	}

	/**
	 * Get the current visibility rules
	 */
	getRules(): VisibilityRule[] {
		return this.rules;
	}

	/**
	 * Set the form group to watch for value changes
	 */
	setFormGroup(formGroup: UntypedFormGroup): void {
		this.formGroup = formGroup;
	}

	/**
	 * Get all field IDs that are being watched (source fields in conditions)
	 */
	getWatchedFields(): string[] {
		const watchedFields = new Set<string>();
		for (const rule of this.rules) {
			for (const condition of rule.conditions) {
				watchedFields.add(condition.field);
			}
		}
		return Array.from(watchedFields);
	}

	/**
	 * Get target fields that depend on a specific source field
	 */
	getTargetFieldsForSource(sourceFieldId: string): string[] {
		return this.rules
			.filter(rule => rule.conditions.some(c => c.field === sourceFieldId))
			.map(rule => rule.targetField);
	}

	/**
	 * Evaluate if a target field should be visible based on current form values.
	 * If multiple rules exist for the same target field, ALL rules must evaluate to true (AND logic).
	 */
	evaluateVisibility(targetFieldId: string): boolean {
		const rulesForField = this.rules.filter(r => r.targetField === targetFieldId);
		
		// No rules means always visible
		if (rulesForField.length === 0) {
			return true;
		}

		// Evaluate each rule - ALL rules must pass for the field to be visible
		for (const rule of rulesForField) {
			// Skip rules with no conditions (they don't affect visibility)
			if (!rule.conditions || rule.conditions.length === 0) {
				continue;
			}

			const logic = rule.logic || 'and';
			const results = rule.conditions.map(condition => this.evaluateCondition(condition));

			let ruleResult: boolean;
			if (logic === 'and') {
				ruleResult = results.every(r => r);
			} else {
				ruleResult = results.some(r => r);
			}

			// If any rule evaluates to false, the field should be hidden
			if (!ruleResult) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Evaluate all rules and return a map of field visibility
	 * Only evaluates non-nested rules (direct field rules)
	 */
	evaluateAllRules(): Map<string, boolean> {
		const visibilityMap = new Map<string, boolean>();
		
		// Collect unique target fields (excluding nested rules)
		const targetFields = new Set<string>();
		for (const rule of this.rules) {
			if (!this.isNestedRule(rule.targetField)) {
				targetFields.add(rule.targetField);
			}
		}
		
		// Evaluate each unique target field once
		for (const targetField of targetFields) {
			visibilityMap.set(targetField, this.evaluateVisibility(targetField));
		}
		
		return visibilityMap;
	}

	/**
	 * Get all parent field IDs that have nested rules
	 */
	getParentFieldsWithNestedRules(): string[] {
		const parentFields = new Set<string>();
		for (const rule of this.rules) {
			if (this.isNestedRule(rule.targetField)) {
				const parsed = this.parseNestedRule(rule);
				if (parsed) {
					parentFields.add(parsed.parentField);
				}
			}
		}
		return Array.from(parentFields);
	}

	/**
	 * Check if a field has visibility rules defined
	 */
	hasRulesForField(fieldId: string): boolean {
		return this.rules.some(r => r.targetField === fieldId);
	}

	/**
	 * Check if a targetField is a nested rule (contains a dot)
	 */
	isNestedRule(targetField: string): boolean {
		return targetField.includes('.');
	}

	/**
	 * Parse a nested rule into its components
	 */
	parseNestedRule(rule: VisibilityRule): NestedVisibilityRule | null {
		if (!this.isNestedRule(rule.targetField)) {
			return null;
		}
		const parts = rule.targetField.split('.');
		return {
			parentField: parts[0],
			childField: parts.slice(1).join('.'),
			rule: rule
		};
	}

	/**
	 * Get all nested rules for a specific parent field
	 */
	getNestedRulesForField(parentFieldId: string): NestedVisibilityRule[] {
		return this.rules
			.filter(r => this.isNestedRule(r.targetField))
			.map(r => this.parseNestedRule(r))
			.filter((nr): nr is NestedVisibilityRule => nr !== null && nr.parentField === parentFieldId);
	}

	/**
	 * Evaluate visibility for a nested rule (using parent form for conditions)
	 */
	evaluateNestedVisibility(nestedRule: NestedVisibilityRule): boolean {
		const rule = nestedRule.rule;
		
		// No conditions means always visible
		if (!rule.conditions || rule.conditions.length === 0) {
			return true;
		}

		const logic = rule.logic || 'and';
		const results = rule.conditions.map(condition => this.evaluateCondition(condition));

		if (logic === 'and') {
			return results.every(r => r);
		} else {
			return results.some(r => r);
		}
	}

	/**
	 * Evaluate all nested rules for a parent field and return a map of child field visibility
	 */
	evaluateNestedRulesForField(parentFieldId: string): Map<string, boolean> {
		const visibilityMap = new Map<string, boolean>();
		const nestedRules = this.getNestedRulesForField(parentFieldId);
		
		for (const nestedRule of nestedRules) {
			visibilityMap.set(nestedRule.childField, this.evaluateNestedVisibility(nestedRule));
		}
		
		return visibilityMap;
	}

	/**
	 * Evaluate a single condition
	 */
	private evaluateCondition(condition: VisibilityCondition): boolean {
		if (!this.formGroup) {
			return true;
		}

		const control = this.formGroup.get(condition.field);
		if (!control) {
			return true; // Field not found, don't hide
		}

		const fieldValue = control.value;
		return this.compare(fieldValue, condition.operator, condition.value);
	}

	/**
	 * Compare values based on operator
	 */
	private compare(fieldValue: any, operator: VisibilityOperator, compareValue: any): boolean {
		switch (operator) {
			case 'equals':
				return this.areEqual(fieldValue, compareValue);
			
			case 'notEquals':
				return !this.areEqual(fieldValue, compareValue);
			
			case 'contains':
				if (Array.isArray(fieldValue)) {
					return fieldValue.includes(compareValue);
				}
				if (typeof fieldValue === 'string') {
					return fieldValue.includes(compareValue);
				}
				return false;
			
			case 'notContains':
				if (Array.isArray(fieldValue)) {
					return !fieldValue.includes(compareValue);
				}
				if (typeof fieldValue === 'string') {
					return !fieldValue.includes(compareValue);
				}
				return true;
			
			case 'greaterThan':
				return this.toNumber(fieldValue) > this.toNumber(compareValue);
			
			case 'lessThan':
				return this.toNumber(fieldValue) < this.toNumber(compareValue);
			
			case 'greaterThanOrEqual':
				return this.toNumber(fieldValue) >= this.toNumber(compareValue);
			
			case 'lessThanOrEqual':
				return this.toNumber(fieldValue) <= this.toNumber(compareValue);
			
			case 'isEmpty':
				return this.isEmpty(fieldValue);
			
			case 'isNotEmpty':
				return !this.isEmpty(fieldValue);
			
			case 'isTrue':
				return fieldValue === true || fieldValue === 'true' || fieldValue === 1;
			
			case 'isFalse':
				return fieldValue === false || fieldValue === 'false' || fieldValue === 0 || this.isEmpty(fieldValue);
			
			default:
				return true;
		}
	}

	private areEqual(a: any, b: any): boolean {
		// Handle null/undefined
		if (isNullOrUndefined(a) && isNullOrUndefined(b)) {
			return true;
		}
		if (isNullOrUndefined(a) || isNullOrUndefined(b)) {
			return false;
		}
		
		// Handle arrays
		if (Array.isArray(a) && Array.isArray(b)) {
			return JSON.stringify(a.sort()) === JSON.stringify(b.sort());
		}
		
		// Handle type coercion for numbers/strings
		// eslint-disable-next-line eqeqeq
		return a == b;
	}

	private isEmpty(value: any): boolean {
		if (isNullOrUndefined(value)) {
			return true;
		}
		if (typeof value === 'string') {
			return value.trim() === '';
		}
		if (Array.isArray(value)) {
			return value.length === 0;
		}
		return false;
	}

	private toNumber(value: any): number {
		if (typeof value === 'number') {
			return value;
		}
		if (value instanceof Date) {
			return value.getTime();
		}
		const parsed = parseFloat(value);
		return isNaN(parsed) ? 0 : parsed;
	}
}
